/**
 * Commander Banners (cosmetic skins) — a second War Funds sink. Purely cosmetic: equipping a
 * banner recolors the commander's heraldry on the menu (and nothing about gameplay), so it is
 * safe to gate behind the meta-currency without affecting balance. Pure config + helpers so the
 * catalog is testable and shared between the shop UI and the menu.
 */
export type SkinId = "default" | "azure" | "crimson" | "emerald" | "violet" | "royal";

export type Skin = {
  id: SkinId;
  /** War Funds price. The default banner is free and always owned. */
  cost: number;
  /** Phaser fill color (0xRRGGBB) for shapes. */
  color: number;
  /** CSS hex for text styles. */
  hex: string;
};

export const SKINS: Record<SkinId, Skin> = {
  default: { id: "default", cost: 0, color: 0xc9b878, hex: "#c9b878" },
  azure: { id: "azure", cost: 300, color: 0x4f9fd8, hex: "#4f9fd8" },
  crimson: { id: "crimson", cost: 350, color: 0xd0584e, hex: "#d0584e" },
  emerald: { id: "emerald", cost: 350, color: 0x57b96a, hex: "#57b96a" },
  violet: { id: "violet", cost: 450, color: 0x9a6fd0, hex: "#9a6fd0" },
  royal: { id: "royal", cost: 600, color: 0xffcf6b, hex: "#ffcf6b" }
};

export const SKIN_IDS: SkinId[] = ["default", "azure", "crimson", "emerald", "violet", "royal"];

export const DEFAULT_SKIN_ID: SkinId = "default";

export function isSkinId(value: unknown): value is SkinId {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(SKINS, value);
}

/** Resolve a (possibly stale/unknown) skin id to its fill color, falling back to the default. */
export function skinColor(id: string | undefined): number {
  return SKINS[isSkinId(id) ? id : DEFAULT_SKIN_ID].color;
}

/** Resolve a (possibly stale/unknown) skin id to its CSS hex, falling back to the default. */
export function skinHex(id: string | undefined): string {
  return SKINS[isSkinId(id) ? id : DEFAULT_SKIN_ID].hex;
}
